import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import Icon from '../components/ui/Icon';
import { FadeInWhenVisible, StaggerContainer, StaggerItem, HoverLift } from '../components/ui/Motion';
import DropZone from '../components/tools/DropZone';
import ToolCard from '../components/tools/ToolCard';
import { POPULAR_TOOLS, TOOL_CATEGORIES, TOOLS, INTEGRATIONS, PRICING_PLANS } from '../data/tools';
import { authActions } from '../store';

/* ─── Hero ─── */
const HeroSection = () => (
  <section className="relative min-h-[92vh] flex items-center gradient-mesh noise-overlay overflow-hidden">
    {/* Decorative elements */}
    <div className="absolute inset-0 pointer-events-none">
      <motion.div animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-24 right-[15%] w-16 h-20 rounded-xl opacity-[0.07] rotate-12"
        style={{ background: '#3374ff' }} />
      <motion.div animate={{ y: [0, 15, 0], rotate: [0, -8, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        className="absolute top-40 left-[10%] w-12 h-16 rounded-lg opacity-[0.06] -rotate-12"
        style={{ background: '#8b5cf6' }} />
      <motion.div animate={{ y: [0, -12, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        className="absolute bottom-32 right-[20%] w-14 h-14 rounded-full opacity-[0.05]"
        style={{ background: '#f9603e' }} />
      {/* Grid lines */}
      <div className="absolute inset-0 opacity-[0.015]"
        style={{ backgroundImage: 'linear-gradient(var(--text-primary) 1px, transparent 1px), linear-gradient(90deg, var(--text-primary) 1px, transparent 1px)', backgroundSize: '64px 64px' }} />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-24 pb-16">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        {/* Left - Copy */}
        <div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <span className="badge-brand mb-4 inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              40+ Document Tools — All Free to Start
            </span>
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-6">
            <span style={{ color: 'var(--text-primary)' }}>Every doc tool{' '}</span>
            <span className="gradient-text">you'll ever need.</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="text-lg md:text-xl max-w-lg mb-8 leading-relaxed"
            style={{ color: 'var(--text-secondary)' }}>
            Convert, merge, compress, sign, and transform documents — all in one beautiful workspace. No installs, no hassle.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
            className="flex flex-wrap gap-3 mb-8">
            {POPULAR_TOOLS.slice(0, 4).map(tool => (
              <Link key={tool.id} to={`/tool/${tool.id}`} className="tool-chip">
                <Icon name={tool.icon} size={15} style={{ color: TOOL_CATEGORIES[tool.category].color }} />
                {tool.name}
              </Link>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="flex items-center gap-4 text-sm" style={{ color: 'var(--text-muted)' }}>
            <div className="flex -space-x-2">
              {['🧑‍💻', '👩‍🎨', '👨‍💼', '👩‍🔬'].map((e, i) => (
                <div key={i} className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm"
                  style={{ borderColor: 'var(--bg-primary)', background: 'var(--bg-secondary)' }}>{e}</div>
              ))}
            </div>
            <span><strong style={{ color: 'var(--text-primary)' }}>52,000+</strong> professionals trust DocuVerse</span>
          </motion.div>
        </div>

        {/* Right - Drop Zone */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 0.5 }}>
          <DropZone />
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─── Tools Bento Grid ─── */
const ToolsBentoSection = () => (
  <section className="py-24 relative" style={{ background: 'var(--bg-secondary)' }}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeInWhenVisible className="text-center mb-16">
        <span className="badge-coral mb-3 inline-block">Full Toolkit</span>
        <h2 className="section-title text-balance">Everything you need, beautifully organized</h2>
        <p className="section-subtitle max-w-2xl mx-auto">
          From simple conversions to advanced document workflows — explore 40+ professional tools.
        </p>
      </FadeInWhenVisible>

      {/* Featured Tools */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {POPULAR_TOOLS.slice(0, 6).map((tool, i) => (
          <FadeInWhenVisible key={tool.id} delay={i * 0.08}>
            <ToolCard tool={tool} variant="featured" />
          </FadeInWhenVisible>
        ))}
      </div>

      {/* Category Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.values(TOOL_CATEGORIES).map((cat, i) => (
          <FadeInWhenVisible key={cat.id} delay={i * 0.06}>
            <Link to={`/tools?cat=${cat.id}`}>
              <HoverLift>
                <div className="card p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ background: cat.bgLight, color: cat.color }}>
                      <Icon name={cat.icon} size={18} />
                    </div>
                    <h3 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{cat.label}</h3>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {TOOLS.filter(t => t.category === cat.id).length} tools
                  </p>
                </div>
              </HoverLift>
            </Link>
          </FadeInWhenVisible>
        ))}
      </div>

      <FadeInWhenVisible className="text-center mt-10">
        <Link to="/tools" className="btn-primary inline-flex items-center gap-2">
          Explore All 40+ Tools <Icon name="ArrowRight" size={16} />
        </Link>
      </FadeInWhenVisible>
    </div>
  </section>
);

/* ─── How It Works ─── */
const HowItWorks = () => {
  const steps = [
    { num: '01', title: 'Choose Your Tool', desc: 'Pick from 40+ document tools or just drop your file — we auto-detect the best action.', icon: 'Search', color: '#3374ff' },
    { num: '02', title: 'Upload & Configure', desc: 'Drag and drop files or import from cloud storage. Adjust settings to your needs.', icon: 'Upload', color: '#8b5cf6' },
    { num: '03', title: 'Process Instantly', desc: 'Our servers convert, merge, compress, or transform your files in seconds.', icon: 'Zap', color: '#f9603e' },
    { num: '04', title: 'Download & Share', desc: 'Download results, save to cloud, or share via email — your choice.', icon: 'Download', color: '#10b981' },
  ];

  return (
    <section className="py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeInWhenVisible className="text-center mb-16">
          <span className="badge-mint mb-3 inline-block">Simple Workflow</span>
          <h2 className="section-title">Four steps to document freedom</h2>
          <p className="section-subtitle max-w-xl mx-auto">No installs, no accounts required. Just pure productivity.</p>
        </FadeInWhenVisible>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <FadeInWhenVisible key={step.num} delay={i * 0.1}>
              <div className="relative text-center group">
                {/* Connector line */}
                {i < 3 && (
                  <div className="hidden lg:block absolute top-12 left-[60%] w-[80%] h-px"
                    style={{ background: `linear-gradient(90deg, ${step.color}30, transparent)` }} />
                )}
                <div className="relative inline-flex mb-6">
                  <div className="w-24 h-24 rounded-3xl flex items-center justify-center transition-transform group-hover:scale-105"
                    style={{ background: `${step.color}10` }}>
                    <Icon name={step.icon} size={36} style={{ color: step.color }} />
                  </div>
                  <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ background: step.color }}>{step.num}</span>
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{step.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{step.desc}</p>
              </div>
            </FadeInWhenVisible>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Integrations ─── */
const IntegrationsSection = () => (
  <section className="py-24 relative overflow-hidden" style={{ background: 'var(--bg-secondary)' }}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeInWhenVisible className="text-center mb-16">
        <span className="badge-brand mb-3 inline-block">Integrations</span>
        <h2 className="section-title">Connected to your favorite services</h2>
        <p className="section-subtitle max-w-xl mx-auto">Import from and export to the cloud storage platforms you already use.</p>
      </FadeInWhenVisible>

      <FadeInWhenVisible className="flex flex-wrap justify-center gap-6">
        {INTEGRATIONS.map((int, i) => (
          <motion.div key={int.id} whileHover={{ y: -4, scale: 1.02 }}
            className="card px-8 py-6 flex flex-col items-center gap-3 min-w-[140px]">
            <span className="text-4xl">{int.icon}</span>
            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{int.name}</span>
          </motion.div>
        ))}
        <motion.div whileHover={{ y: -4 }}
          className="card px-8 py-6 flex flex-col items-center gap-3 min-w-[140px] cursor-pointer"
          style={{ borderStyle: 'dashed' }}>
          <div className="text-4xl opacity-40">+</div>
          <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>More soon</span>
        </motion.div>
      </FadeInWhenVisible>
    </div>
  </section>
);

/* ─── Features Highlights ─── */
const FeaturesHighlight = () => {
  const features = [
    { title: 'Bank-Grade Security', desc: 'All files encrypted with AES-256. Auto-deleted after 2 hours. Your privacy is our priority.', icon: 'Shield', color: '#ef4444' },
    { title: 'Lightning Fast', desc: 'Process documents in seconds, not minutes. Our optimized servers handle millions of files daily.', icon: 'Zap', color: '#f59e0b' },
    { title: 'Batch Processing', desc: 'Convert, compress, or merge hundreds of files at once with our powerful batch engine.', icon: 'Layers', color: '#8b5cf6' },
    { title: 'AI-Powered OCR', desc: 'Extract text from scanned documents and images with industry-leading accuracy.', icon: 'Eye', color: '#3374ff' },
    { title: 'Smart Workflows', desc: 'Chain multiple operations — merge, then compress, then watermark — in a single flow.', icon: 'RefreshCw', color: '#10b981' },
    { title: 'Email Integration', desc: 'Send converted files directly via email. Perfect for sharing with clients and colleagues.', icon: 'Mail', color: '#06b6d4' },
  ];

  return (
    <section className="py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeInWhenVisible className="text-center mb-16">
          <span className="badge-brand mb-3 inline-block">Why DocuVerse?</span>
          <h2 className="section-title">Built for the way you work</h2>
          <p className="section-subtitle max-w-xl mx-auto">Professional-grade features that make document management effortless.</p>
        </FadeInWhenVisible>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <FadeInWhenVisible key={f.title} delay={i * 0.08}>
              <HoverLift>
                <div className="card p-6 h-full">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ background: `${f.color}10`, color: f.color }}>
                    <Icon name={f.icon} size={24} />
                  </div>
                  <h3 className="text-base font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{f.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{f.desc}</p>
                </div>
              </HoverLift>
            </FadeInWhenVisible>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Pricing ─── */
const PricingSection = () => {
  const dispatch = useDispatch();
  return (
    <section className="py-24" style={{ background: 'var(--bg-secondary)' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeInWhenVisible className="text-center mb-16">
          <span className="badge-coral mb-3 inline-block">Pricing</span>
          <h2 className="section-title">Start free, scale as you grow</h2>
          <p className="section-subtitle max-w-xl mx-auto">5 free documents — no credit card required. Upgrade anytime.</p>
        </FadeInWhenVisible>

        <div className="grid md:grid-cols-3 gap-6">
          {PRICING_PLANS.map((plan, i) => (
            <FadeInWhenVisible key={plan.id} delay={i * 0.1}>
              <div className={`card p-6 h-full flex flex-col ${plan.popular ? 'ring-2' : ''}`}
                style={plan.popular ? { borderColor: '#3374ff', boxShadow: '0 0 40px -8px rgba(51,116,255,0.2)' } : {}}>
                {plan.popular && (
                  <span className="badge-brand self-start mb-4">Most Popular</span>
                )}
                <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{plan.name}</h3>
                <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>{plan.desc}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold" style={{ color: 'var(--text-primary)' }}>
                    {plan.price === 0 ? 'Free' : `$${plan.price}`}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-sm ml-1" style={{ color: 'var(--text-muted)' }}>/{plan.period}</span>
                  )}
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <Icon name="Check" size={16} className="flex-shrink-0 mt-0.5" style={{ color: '#10b981' }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => plan.id === 'free' && dispatch(authActions.setShowAuthModal(true))}
                  className={plan.popular ? 'btn-primary w-full' : 'btn-secondary w-full'}>
                  {plan.cta}
                </button>
              </div>
            </FadeInWhenVisible>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── CTA ─── */
const CTASection = () => {
  const dispatch = useDispatch();
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <FadeInWhenVisible>
          <div className="relative rounded-3xl p-12 md:p-16 overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #0f1225 0%, #1a1f3a 100%)' }}>
            {/* Decorative */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full" style={{ background: 'rgba(51,116,255,0.15)' }} />
              <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full" style={{ background: 'rgba(139,92,246,0.1)' }} />
            </div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 text-balance">
                Ready to transform your document workflow?
              </h2>
              <p className="text-lg text-white/60 mb-8 max-w-lg mx-auto">
                Join 52,000+ professionals who save hours every week with DocuVerse.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button onClick={() => dispatch(authActions.setShowAuthModal(true))}
                  className="btn-primary !px-8 !py-4 text-base">
                  Get Started Free <Icon name="ArrowRight" size={18} className="ml-2" />
                </button>
                <Link to="/tools" className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors font-medium">
                  Explore Tools <Icon name="ExternalLink" size={16} />
                </Link>
              </div>
            </div>
          </div>
        </FadeInWhenVisible>
      </div>
    </section>
  );
};

/* ─── Home Page ─── */
const HomePage = () => (
  <>
    <HeroSection />
    <ToolsBentoSection />
    <HowItWorks />
    <FeaturesHighlight />
    <IntegrationsSection />
    <PricingSection />
    <CTASection />
  </>
);

export default HomePage;
